import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  Box, TextField, Button, Typography, CircularProgress, IconButton, InputAdornment 
} from "@mui/material";
import { FaKey } from "react-icons/fa"; // Icon for generating password
import axiosInstance from "../axiosInstance";
import { toast } from "react-toastify";

const apiUrl = process.env.REACT_APP_API_URL;

const UserViewPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [user, setUser] = useState({
    id: "",
    username: "",
    email: "",
  });

  const [password, setPassword] = useState(""); // Separate state for password
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchUser();
  }, []);

  const fetchUser = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");

      if (!token) {
        navigate("/login", { replace: true });
        return;
      }

      const response = await axiosInstance.get(`${apiUrl}/user/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.user) {
        setUser({
          id: response.data.user.id || "",
          username: response.data.user.username || "",
          email: response.data.user.email || "",
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to fetch user data");
    } finally {
      setLoading(false);
    }
  };

  const generatePassword = () => {
    const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*";
    let generatedPassword = "";
    for (let i = 0; i < 10; i++) {
      generatedPassword += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    setPassword(generatedPassword);
  };

  const handleChange = (e) => {
    setUser({ ...user, [e.target.name]: e.target.value });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setErrors({});

    if (!user.username || !user.email) {
      setErrors({
        username: !user.username ? "Username is required" : "",
        email: !user.email ? "Email is required" : "",
      });
      return;
    }

    try {
      setUpdating(true);
      const token = localStorage.getItem("authToken");

      const updateData = {
        username: user.username,
        email: user.email,
      };

      // Include password only if it's changed
      if (password) {
        updateData.password = password;
      }

      const response = await axiosInstance.put(
        `${apiUrl}/user/${id}`,
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(response.data.message || "User updated successfully!");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update user");
    } finally {
      setUpdating(false);
    }
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      sx={{ bgcolor: "#f7f7f7", p: 3 }}
    >
      <Typography variant="h4" gutterBottom>
        User Details
      </Typography>

      {loading ? (
        <CircularProgress />
      ) : (
        <form onSubmit={handleUpdate}>
         
          <TextField
            label="Username"
            variant="outlined"
            margin="normal"
            fullWidth
            name="username"
            value={user.username}
            onChange={handleChange}
            error={Boolean(errors.username)}
            helperText={errors.username}
          />
          <TextField
            label="Email"
            variant="outlined"
            margin="normal"
            fullWidth
            name="email"
            value={user.email}
            onChange={handleChange}
            error={Boolean(errors.email)}
            helperText={errors.email}
          />
          <TextField
            label="Password"
            type="text"
            variant="outlined"
            margin="normal"
            fullWidth
            name="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={generatePassword} edge="end">
                    <FaKey />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            fullWidth
            disabled={updating}
            sx={{ mt: 2 }}
          >
            {updating ? <CircularProgress size={24} color="inherit" /> : "Update User"}
          </Button>
        </form>
      )}
    </Box>
  );
};

export default UserViewPage;
